import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { isLinkedReferenceGridLayoutItem } from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import { isListCatalogNameGridId } from './listCatalogNameGrid'
import { isParentObjectNameGridId } from './parentObjectNameGrid'
import { parseRefHopChainFromGridId } from './passportRefGridLinkedParse'

/**
 * Собирает id `list_types`-рёбер корневого типа, которые реально нужны раскладке паспорта
 * (ячейки «Справочники» / ref / parent-acc), а не все поля типа.
 */
export function collectRuntimeParentAccordionRoots(
  gridTemplate: GridFieldType[],
  ownFields: ObjectInventoryField[],
  catalogTypeId: number,
): ObjectInventoryField[] {
  if (!Number.isFinite(catalogTypeId) || catalogTypeId <= 0 || !gridTemplate.length) {
    return []
  }

  const requiredEdgeIds = new Set<string>()

  for (const item of gridTemplate) {
    const gid = String(item.id ?? '')

    if (isParentObjectNameGridId(gid) || isListCatalogNameGridId(gid)) continue

    const palette = item.palette_source
    const linkedHint = isLinkedReferenceGridLayoutItem(item)
    const isDictionaryCell =
      palette === 'parent' || linkedHint || gid.startsWith('parent-acc-') || gid.startsWith('ref:')
    if (!isDictionaryCell) continue

    for (const seg of parseRefHopChainFromGridId(gid)) {
      if (seg.object_type_id === catalogTypeId) {
        requiredEdgeIds.add(String(seg.field_id))
      }
    }

    const parentAccMatch = /^parent-acc-\d+-([A-Za-z0-9]+)-root::/.exec(gid)
    if (parentAccMatch?.[1]) requiredEdgeIds.add(parentAccMatch[1])

    const segs = item.nested_binding?.segments
    if (Array.isArray(segs)) {
      for (const seg of segs) {
        if (Number(seg.object_type_id) === catalogTypeId && seg.field_id != null) {
          requiredEdgeIds.add(String(seg.field_id))
        }
      }
    }

    const inv = (item as GridFieldType & { inventory_field_id?: string | number })
      .inventory_field_id
    if (inv != null && (palette === 'parent' || linkedHint)) {
      requiredEdgeIds.add(String(inv))
    }

    /** `parent:<leafId>:<uuid>` — leaf на родительском типе; ребро — own `list_types` → inventory_type. */
    const parentPrefixed = /^parent:([^:]+):.+$/.exec(gid)
    if (parentPrefixed || palette === 'parent') {
      const invType = Number(item.inventory_type)
      if (Number.isFinite(invType) && invType > 0 && invType !== catalogTypeId) {
        for (const field of ownFields) {
          if (field.type !== 'list_types') continue
          if (Number(field.object_type) === invType) {
            requiredEdgeIds.add(String(field.id))
          }
        }
      }
    }
  }

  if (requiredEdgeIds.size === 0) return []

  return ownFields.filter((field) => {
    if (field.type !== 'list_types' || Number(field.object_type) <= 0) return false
    return requiredEdgeIds.has(String(field.id))
  })
}

/** Стабильный ключ для watch: только list_types-поля, влияющие на загрузку веток. */
export function runtimeParentAccordionInputsFingerprint(
  ownFields: ObjectInventoryField[],
  groupId: string,
  catalogTypeId: number,
  fieldGroups: Array<{
    id?: number
    type?: string
    list_invent_object_type_id?: number | null
  }>,
  gridTemplate: GridFieldType[] | null | undefined,
): string {
  const roots = collectRuntimeParentAccordionRoots(gridTemplate ?? [], ownFields, catalogTypeId)
  const rootsKey = roots
    .map((f) => `${String(f.id)}:${Number(f.object_type)}`)
    .sort()
    .join('|')
  const groupsKey = fieldGroups
    .map((g) => `${g.id ?? 0}:${g.type ?? ''}:${g.list_invent_object_type_id ?? 0}`)
    .sort()
    .join('|')
  return [groupId, String(catalogTypeId), rootsKey, groupsKey].join(';')
}
